/**
 * The ONE place that decides what pressing "TV" does (TV-6).
 *
 * ## Why this file exists
 *
 * Before it, the answer was a hardcoded Android package name duplicated across
 * five call sites - `_shared/launcher/kit.tsx`, Atelier, Mosaic, Spectrum, and
 * an inline string literal in CareScheme that carried a comment asking the next
 * reader to keep it in sync by hand. Two consequences, both fixed here:
 *
 * 1. **The global `tvStreamingAppSlug` setting had no effect on launching.**
 *    A property that changed the streaming app in the CMS still got the
 *    hardcoded default, because the launcher never read the setting - a
 *    standing gap since the setting was added. The guest-web companion already
 *    honoured it (`shared/guest-content.ts`); the television did not.
 * 2. **There was no per-premise lever at all** - see
 *    [[parallel-operation-contract]], which recorded "no client-side lever
 *    exists" as a standing fact of the programme. `PremiseConfig.tvPlayer` is
 *    that lever, and this module is where it is read.
 *
 * ## The rules this module obeys
 *
 * - **Fail safe to the vendor app.** Absent value, unknown string, config that
 *   has not loaded, built-in player not available on this build - all of them
 *   resolve to `{kind:"vendor"}`. A guest must never press TV and get nothing.
 * - **A half-landed flip fails to the OLD behaviour.** The choice is read at
 *   PRESS time from the config the TV has actually got. A `config_changed` push
 *   that never arrives (see [[tv-realtime-sync]]: the hasselbacken channel stuck
 *   for 25+ minutes, three times in one day, and a remote `reboot` rides the
 *   same channel so it cannot be relied on to rescue it) leaves the OLD config
 *   in place, and the tile therefore keeps doing the OLD thing. Never a black
 *   screen and never a half-applied state; the 5-minute `FALLBACK_POLL_MS`
 *   re-fetch in `useConfig` is the outer bound on how long the stale answer
 *   lasts.
 * - **The tile is not toggleable and stays that way.** `LAUNCHER_FUNCTIONS`
 *   ("TV is always first and always present") and `HOME_KNOBS` ("TV ... isn't
 *   toggleable in either family") are both still true: this changes the tile's
 *   DESTINATION, never its presence.
 * - **The built-in destination is injected, not imported.** The `vod`/`guide`
 *   screens are a separate unit of work and do not exist yet. A caller that has
 *   one passes `openBuiltin`; a caller that has not passes nothing and gets the
 *   vendor app. So this module is complete and correct on its own, and hooking
 *   the screens up later is a change at the call site, not here.
 */
import type { PremiseConfig, TvPlayerChoice } from "@shared/types/foyer";
import {
  DEFAULT_TV_PLAYER,
  DEFAULT_TV_STREAMING_SLUG,
  isTvPlayerChoice,
} from "@shared/types/foyer";
import { launchApp } from "./launchApp";
import { durableGetRaw, getBinding } from "./binding";
import { CONFIG_CACHE_PREFIX } from "../hooks/useConfig";

/** Where the "TV" tile is about to send the screen. */
export type TvTarget =
  | { kind: "vendor"; androidSlug: string }
  | { kind: "builtin" };

export interface ResolveTvOptions {
  /**
   * Does this build actually HAVE a built-in player to open? Default `false`.
   *
   * Deliberately a capability flag rather than a platform check, matching the
   * FoyerBridge rule: a missing capability hides its surface uniformly. While
   * it is false, `tvPlayer: "foyer"` is a recorded preference that nothing can
   * act on yet - which is the correct behaviour, not a bug: an operator who
   * flips the switch early gets the vendor app, not a blank screen.
   */
  builtinAvailable?: boolean;
}

/**
 * The vendor app's package name for this premise.
 *
 * Honours the global `tvStreamingAppSlug` (injected into every premise payload
 * by `GET /api/foyer/premise/:id`) and falls back to the shared default. Blank
 * and whitespace-only slugs count as unset - `settingsStore` already normalises
 * `"  "` to null on write, and a config cached before it did must not turn into
 * an intent to launch a package named "".
 */
function vendorSlug(config: PremiseConfig | null | undefined): string {
  const slug = config?.tvStreamingAppSlug?.trim();
  return slug || DEFAULT_TV_STREAMING_SLUG;
}

/**
 * Decide where "TV" goes. Pure - no side effects, no globals - so the whole
 * decision table is unit-testable without a bridge, a DOM or a network.
 */
export function resolveTvTarget(
  config: PremiseConfig | null | undefined,
  opts?: ResolveTvOptions
): TvTarget {
  const vendor: TvTarget = { kind: "vendor", androidSlug: vendorSlug(config) };
  // Checked BEFORE the premise's choice, so a premise set to "foyer" on a build
  // with no built-in player still opens something.
  if (!opts?.builtinAvailable) return vendor;
  const choice = isTvPlayerChoice(config?.tvPlayer)
    ? config.tvPlayer
    : DEFAULT_TV_PLAYER;
  return choice === "foyer" ? { kind: "builtin" } : vendor;
}

export interface OpenTvDeps {
  /**
   * Open Foyer's own player. Its PRESENCE is what makes the built-in branch
   * reachable at all (see {@link ResolveTvOptions.builtinAvailable}).
   */
  openBuiltin?: () => void;
  /**
   * The premise's choice from the config this render is USING, rather than
   * from the durable cache.
   *
   * Used only when the cache holds no valid choice — a bound television's own
   * cached config still wins, because that is the config it has actually
   * received. This exists for the CMS preview, which has a real config and no
   * pairing binding to key a cache read on.
   */
  tvPlayer?: TvPlayerChoice;
  /**
   * Launch an installed app. Defaults to {@link launchApp} - the single choke
   * point every app tile goes through, which is also where a launch is counted
   * for content-engagement (`app_launch` + the `app_dwell` timer, for apps
   * carrying a catalogue `id`).
   *
   * The TV tile is not a catalogue app and has never passed an `id`, so it is
   * not attributed today and this change does not alter that either way - the
   * vendor branch goes through exactly the same call it always did. Injectable
   * so tests can observe the launch without a bridge.
   */
  launch?: (app: { name: string; androidSlug: string }) => boolean;
}

/**
 * Hand the screen to whatever this premise's TV tile points at, and report
 * which one that was (the return value is for tests and diagnostics; call sites
 * ignore it).
 */
export function openTv(
  config: PremiseConfig | null | undefined,
  deps?: OpenTvDeps
): TvTarget {
  const openBuiltin = deps?.openBuiltin;
  const target = resolveTvTarget(config, {
    builtinAvailable: typeof openBuiltin === "function",
  });
  if (target.kind === "builtin") {
    // Non-null by construction: `builtinAvailable` was derived from it.
    openBuiltin!();
    return target;
  }
  // On web / CMS preview there is nothing to launch, so this is a no-op that
  // returns false and the launcher simply stays put.
  (deps?.launch ?? launchApp)({ name: "TV", androidSlug: target.androidSlug });
  return target;
}

/**
 * The premise config THIS television is currently running on, read at press
 * time from the last-good copy `useConfig` persists on every successful fetch.
 *
 * ## Why read a cache instead of taking a prop
 *
 * The scheme components receive only a `ResolvedScheme` (`SchemeProps`), never
 * the `PremiseConfig` - `SchemeRouter` resolves the one from the other and
 * keeps the config to itself. Threading a second prop through the router and
 * every template (nine of them, plus the shared launcher kit) to deliver two
 * scalar fields is a far larger and more collision-prone change than the lever
 * itself, so the launch sites ask for the config here instead.
 *
 * That is safe, and slightly better than a prop, because:
 *
 * - `useConfig` writes this cache in the SAME tick it calls `setConfig`, so it
 *   cannot disagree with what is on screen, and it cannot be newer.
 * - It is read on the key press, so there is no stale React closure to capture
 *   a config from before the last push.
 * - Every failure mode - no binding (CMS preview), no cache entry, unparseable
 *   JSON - returns `null`, which {@link resolveTvTarget} reads as the vendor
 *   app.
 *
 * `migrateConfig` is deliberately NOT run: we read two scalar fields that no
 * migration has ever touched, and running the full migration chain on a
 * several-hundred-kilobyte config on a TV's WebView, on the frame before we
 * hand the screen away, would be paid for by the guest.
 */
let memoRaw: string | null = null;
let memoConfig: PremiseConfig | null = null;

export function currentTvConfig(): PremiseConfig | null {
  try {
    const premiseId = getBinding()?.premiseId;
    if (!premiseId) return null;
    const raw = durableGetRaw(`${CONFIG_CACHE_PREFIX}${premiseId}`);
    if (!raw) {
      memoRaw = null;
      memoConfig = null;
      return null;
    }
    // Keyed on the raw text, so a config push invalidates the memo by changing
    // it. Exists so a guest jabbing the TV button does not re-parse a few
    // hundred kilobytes per press.
    if (raw === memoRaw) return memoConfig;
    const parsed = JSON.parse(raw) as
      | { config?: PremiseConfig }
      | PremiseConfig;
    // Envelope (`{savedAt, config}`) since 2026; bare configs written by older
    // builds are still on disk in the field, so both shapes are accepted -
    // the same tolerance `useConfig.loadCached` has.
    const envelope = parsed as { config?: PremiseConfig };
    const cfg =
      envelope && typeof envelope === "object" && envelope.config
        ? envelope.config
        : (parsed as PremiseConfig);
    memoRaw = raw;
    memoConfig = cfg && typeof cfg === "object" ? cfg : null;
    return memoConfig;
  } catch {
    return null;
  }
}

/**
 * {@link openTv} against this television's own config. This is what the five
 * launcher/scheme call sites use.
 */
/**
 * Press TV, using the config this render is actually using where there is one.
 *
 * 🚨 `deps.tvPlayer` WINS over the cached config, and that is the whole point.
 *
 * `currentTvConfig()` reads the durable cache keyed by the pairing binding's
 * premise. A CMS preview has no binding, so it read `null`, fell through to the
 * `norigin` default and tried to launch an Android package — on the web that is
 * a silent no-op, so the TV tile did nothing at all on a premise explicitly set
 * to the built-in player. The config the app is RENDERING with is the more
 * truthful of the two: on a bound television they agree, and in a preview it is
 * the only one that exists.
 */
export function openTvForThisScreen(deps?: OpenTvDeps): TvTarget {
  const cached = currentTvConfig();
  const config =
    deps?.tvPlayer && !isTvPlayerChoice(cached?.tvPlayer)
      ? ({ ...(cached ?? {}), tvPlayer: deps.tvPlayer } as PremiseConfig)
      : cached;
  return openTv(config, deps);
}
